import React from 'react'
import bannerData from '../../src/data/content/bannerData.json'

interface BannerSpec {
  dimensions: string
  format: string
  maxSize: string
  codeExample: string
  previewWidth: number
  previewHeight: number
}

const BannerPage: React.FC = () => {
  const renderSpecifications = (specs: BannerSpec) => (
    <>
      <h3 className='font-medium text-base md:text-lg text-anthracite mb-3'>
        Spécifications :
      </h3>
      <ul className='list-none list-inside text-gray-600 mb-4 text-sm md:text-base'>
        <li>Dimensions : {specs.dimensions}</li>
        <li>Format : {specs.format}</li>
        <li>Poids maximal : {specs.maxSize}</li>
      </ul>
    </>
  )

  const renderCodeExample = (specs: BannerSpec) => (
    <div className='bg-white p-3 md:p-4 rounded border border-gray-300 mb-4'>
      <pre className='text-xs md:text-sm text-gray-700 overflow-x-auto whitespace-pre-wrap break-all md:break-normal'>
        <code>{specs.codeExample}</code>
      </pre>
    </div>
  )

  const renderPreview = (title: string, specs: BannerSpec) => (
    <div className='overflow-x-auto pb-4'>
      <div
        className='border-2 border-dashed border-gray-300 rounded max-w-full mx-auto flex items-center justify-center text-gray-500 text-sm md:text-base min-w-[320px]'
        style={{
          height: `${specs.previewHeight}px`,
          width: `${specs.previewWidth}px`,
        }}
      >
        Exemple visuel - {title}
      </div>
    </div>
  )

  return (
    <>
      <div className='bg-redpink text-white uppercase text-center lg:text-2xl py-2  mb-5'>
        Nos bannières
      </div>
      <article className='mx-auto max-w-7xl px-2 lg:px-0'>
        <section className='mb-8 md:mb-12'>
          <h2 className='text-lg md:text-xl lg:text-2xl font-semibold text-redpink mb-4 md:mb-6'>
            {bannerData.title}
          </h2>
          <p className='text-anthracite leading-relaxed mb-6 md:mb-8'>
            {bannerData.description}
          </p>
        </section>

        {bannerData.bannerTypes.map(
          (
            bannerType: {
              id: React.Key | null | undefined
              title: string
              specs: BannerSpec
            },
            index: number,
          ) => (
            <section key={bannerType.id} className='mb-8 md:mb-12'>
              <h2 className='text-lg md:text-xl lg:text-2xl font-semibold text-redpink mb-4 md:mb-6'>
                {`${index + 1}. ${bannerType.title}`}
              </h2>
              <div className='bg-grisclair p-4 md:p-6 rounded-lg'>
                {renderSpecifications(bannerType.specs)}
                {renderCodeExample(bannerType.specs)}
                {renderPreview(bannerType.title, bannerType.specs)}
              </div>
            </section>
          ),
        )}

        <section className='mb-8 md:mb-12'>
          <h2 className='text-lg md:text-xl lg:text-2xl font-semibold text-redpink mb-4 md:mb-6'>
            Informations importantes
          </h2>
          <div className='bg-gray-50 p-4 md:p-6 rounded-lg'>
            <ul className='space-y-3 text-gray-600 text-sm md:text-base'>
              {bannerData.importantInfo.map(
                (
                  info: {
                    text:
                      | string
                      | number
                      | boolean
                      | React.ReactElement<
                          any,
                          string | React.JSXElementConstructor<any>
                        >
                      | Iterable<React.ReactNode>
                      | React.ReactPortal
                      | Iterable<React.ReactNode>
                      | null
                      | undefined
                    link?: {
                      url: string | undefined
                      text:
                        | string
                        | number
                        | boolean
                        | React.ReactElement<
                            any,
                            string | React.JSXElementConstructor<any>
                          >
                        | Iterable<React.ReactNode>
                        | React.ReactPortal
                        | Iterable<React.ReactNode>
                        | null
                        | undefined
                    }
                  },
                  index: React.Key | null | undefined,
                ) => (
                  <li key={index} className='block sm:flex items-start'>
                    <span className='text-redpink mr-2'>•</span>

                    {info.text}

                    {info.link && (
                      <a
                        href={info.link.url}
                        className='text-redpink hover:text-redpinkDark transition-colors duration-200 ml-1'
                      >
                        {info.link.text}
                      </a>
                    )}
                  </li>
                ),
              )}
            </ul>
          </div>
        </section>
      </article>
    </>
  )
}

export default BannerPage
